import React, { useState, useEffect, useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import { Loader2, TrendingUp, AlertTriangle, CheckCircle2, ListTodo } from 'lucide-react';
import { Button } from './ui/button';
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from './ui/select';
import { useBoards } from '../context/BoardContext';
import { getAnalytics } from '../lib/api';
import { CATEGORIES, CATEGORY_MAP } from '../lib/categories';

const GRANULARITIES = [
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
];

const AnalyticsView = () => {
  const { activeBoard } = useBoards();
  const [startDate, setStartDate] = useState(dayjs().subtract(30, 'day').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [granularity, setGranularity] = useState('week');
  const [selectedCat, setSelectedCat] = useState('all');
  const [filters, setFilters] = useState({});
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);

  // Value dropdowns are only meaningful for a specific category's
  // select/multiselect detail fields.
  const catFields = useMemo(() => {
    if (selectedCat === 'all') return [];
    return (activeBoard?.category_configs?.[selectedCat]?.fields || [])
      .filter((f) => f.type === 'select' || f.type === 'multiselect');
  }, [activeBoard, selectedCat]);

  const handleFilterChange = useCallback((fieldName, values) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (values.length) {
        next[fieldName] = values;
      } else {
        delete next[fieldName];
      }
      return next;
    });
  }, []);

  const load = useCallback(async () => {
    if (!activeBoard) return;
    setLoading(true);
    try {
      const params = {
        start_date: startDate,
        end_date: endDate,
        granularity,
        ...(selectedCat !== 'all' ? { category: selectedCat } : {}),
        ...(Object.keys(filters).length ? { filters: JSON.stringify(filters) } : {}),
      };
      const data = await getAnalytics(activeBoard.id, params);
      setAnalytics(data);
    } finally {
      setLoading(false);
    }
  }, [activeBoard, startDate, endDate, granularity, selectedCat, filters]);

  useEffect(() => {
    load();
  }, [load]);

  const seriesData = useMemo(() => {
    if (!analytics?.series) return [];
    if (selectedCat === 'all') return analytics.series;
    // Rewrite to show single category's colors
    return analytics.series.map((row) => ({
      period: row.period,
      Green: row[`${selectedCat}_green`] || 0,
      Yellow: row[`${selectedCat}_yellow`] || 0,
      Red: row[`${selectedCat}_red`] || 0,
    }));
  }, [analytics, selectedCat]);

  const byCategoryData = useMemo(() => {
    if (!analytics?.totals?.by_category) return [];
    return CATEGORIES.map((c) => ({
      name: c.label,
      key: c.key,
      color: c.color,
      total: analytics.totals.by_category[c.key] || 0,
    }));
  }, [analytics]);

  const byColorData = useMemo(() => {
    if (!analytics?.totals?.by_color) return [];
    const colors = { green: '#22c55e', yellow: '#eab308', red: '#ef4444', grey: '#94a3b8' };
    return Object.entries(analytics.totals.by_color)
      .filter(([k]) => k !== 'grey')
      .map(([k, v]) => ({ name: k, value: v, color: colors[k] || '#64748b' }));
  }, [analytics]);

  if (!activeBoard) {
    return <div className="p-12 text-center text-slate-500">Select or create a board first.</div>;
  }

  return (
    <div className="p-6 max-w-[1500px] mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Analytics</h1>
          <p className="text-sm text-slate-500 mt-1">
            Trends and distribution across {activeBoard.name}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5">
            <label htmlFor="analytics-start" className="text-xs text-slate-500">From</label>
            <input
              id="analytics-start"
              type="date"
              value={startDate}
              max={endDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="h-9 rounded-md border border-slate-200 px-2 text-sm text-slate-700"
            />
            <label htmlFor="analytics-end" className="text-xs text-slate-500">To</label>
            <input
              id="analytics-end"
              type="date"
              value={endDate}
              min={startDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="h-9 rounded-md border border-slate-200 px-2 text-sm text-slate-700"
            />
          </div>
          <Select value={granularity} onValueChange={setGranularity}>
            <SelectTrigger className="w-[120px] h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {GRANULARITIES.map((g) => (
                <SelectItem key={g.key} value={g.key}>Per {g.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={selectedCat}
            onValueChange={(v) => {
              setSelectedCat(v);
              setFilters({});
            }}
          >
            <SelectTrigger className="w-[160px] h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {CATEGORIES.map((c) => (
                <SelectItem key={c.key} value={c.key}>
                  {c.key} – {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {catFields.length > 0 && (
        <div className="flex flex-wrap items-end gap-3 mb-6">
          {catFields.map((field) => (
            <div key={field.name} className="flex flex-col gap-1">
              <label htmlFor={`filter-${field.name}`} className="text-xs text-slate-500">
                {field.label}
              </label>
              <select
                id={`filter-${field.name}`}
                multiple
                value={filters[field.name] || []}
                onChange={(e) =>
                  handleFilterChange(field.name, Array.from(e.target.selectedOptions, (o) => o.value))
                }
                className="min-w-[150px] h-[72px] rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-700"
              >
                {(field.options || []).map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
          ))}
          {Object.keys(filters).length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-xs text-slate-500"
              onClick={() => setFilters({})}
            >
              Clear filters
            </Button>
          )}
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KpiCard
          label="Total Issues (Red + Yellow)"
          value={
            (analytics?.totals?.by_color?.red || 0) +
            (analytics?.totals?.by_color?.yellow || 0)
          }
          icon={AlertTriangle}
          color="#dc2626"
          bg="#fee2e2"
        />
        <KpiCard
          label="Open Tasks"
          value={analytics?.totals?.open_tasks || 0}
          icon={ListTodo}
          color="#0284c7"
          bg="#e0f2fe"
        />
        <KpiCard
          label="Repeat Issues"
          value={analytics?.totals?.repeats || 0}
          icon={TrendingUp}
          color="#b45309"
          bg="#fef3c7"
        />
        <KpiCard
          label="Closed"
          value={analytics?.totals?.closed_issues || 0}
          icon={CheckCircle2}
          color="#16a34a"
          bg="#dcfce7"
        />
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-slate-500 py-4 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading...
        </div>
      )}

      {/* Trend line/stacked bar */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 mb-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-sm font-semibold text-slate-800">
              Incident trend {selectedCat === 'all' ? 'across all categories' : `for ${CATEGORY_MAP[selectedCat].label}`}
            </div>
            <div className="text-xs text-slate-500">Per {granularity}</div>
          </div>
        </div>
        {seriesData.length === 0 ? (
          <div className="text-center text-slate-500 text-sm py-12">
            No data yet in the selected range. Add some card statuses in the Board view.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={340}>
            {selectedCat === 'all' ? (
              <LineChart data={seriesData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="period" stroke="#64748b" fontSize={11} />
                <YAxis allowDecimals={false} stroke="#64748b" fontSize={11} />
                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0' }} />
                <Legend />
                <Line type="monotone" dataKey="total_red" name="Red" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="total_yellow" name="Yellow" stroke="#eab308" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="total_green" name="Green" stroke="#22c55e" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            ) : (
              <BarChart data={seriesData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="period" stroke="#64748b" fontSize={11} />
                <YAxis allowDecimals={false} stroke="#64748b" fontSize={11} />
                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0' }} />
                <Legend />
                <Bar dataKey="Red" stackId="a" fill="#ef4444" />
                <Bar dataKey="Yellow" stackId="a" fill="#eab308" />
                <Bar dataKey="Green" stackId="a" fill="#22c55e" />
              </BarChart>
            )}
          </ResponsiveContainer>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {/* By category */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
          <div className="text-sm font-semibold text-slate-800 mb-3">
            Cards by Category
          </div>
          {byCategoryData.every((d) => d.total === 0) ? (
            <div className="text-center text-slate-500 text-sm py-10">No data yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={byCategoryData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" allowDecimals={false} stroke="#64748b" fontSize={11} />
                <YAxis dataKey="key" type="category" width={35} stroke="#64748b" fontSize={12} />
                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0' }} />
                <Bar dataKey="total" name="Total cards">
                  {byCategoryData.map((d) => (
                    <Cell key={d.key} fill={d.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Color distribution */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
          <div className="text-sm font-semibold text-slate-800 mb-3">
            Status Distribution
          </div>
          {byColorData.length === 0 ? (
            <div className="text-center text-slate-500 text-sm py-10">No data yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={byColorData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={95}
                  label={(entry) => `${entry.name}: ${entry.value}`}
                >
                  {byColorData.map((d, i) => (
                    <Cell key={i} fill={d.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
};

const KpiCard = ({ label, value, icon: Icon, color, bg }) => (
  <div
    className="rounded-lg border p-4 flex items-center gap-3"
    style={{ background: bg, borderColor: color + '40' }}
  >
    <div
      className="w-10 h-10 rounded-md flex items-center justify-center"
      style={{ background: color + '20' }}
    >
      <Icon className="h-5 w-5" style={{ color }} />
    </div>
    <div>
      <div className="text-xs font-medium" style={{ color }}>
        {label}
      </div>
      <div className="text-2xl font-bold text-slate-800">{value}</div>
    </div>
  </div>
);

export default AnalyticsView;
